/* SpecSheetBody — pure-presentation spec sheet content (used by both the
   modal popup and the standalone /infoshop/product/:pn/spec page). Receives a
   fully-loaded product object — no fetching here. */
import React from "react";
import {
  FileText,
  Layers,
  Box,
  Clock,
  Tag,
  CircleCheck,
  Image as ImageIcon,
} from "lucide-react";
import ProductVisual from "./ProductVisual";

function Field({ label, value, mono = false, testid }) {
  return (
    <div className="border-b border-slate-200 py-2.5 flex items-baseline gap-4">
      <div className="w-44 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 shrink-0">
        {label}
      </div>
      <div
        className={`text-sm text-slate-900 ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      >
        {value || <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}

export default function SpecSheetBody({ product, compact = false }) {
  const pct = (v) => (v == null ? "—" : `${v}%`);
  const money = (v) =>
    v == null
      ? "—"
      : `${product.currency} ${Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

  return (
    <div data-testid="spec-sheet-body">
      {/* Document header */}
      <header className="border-b-2 border-slate-900 pb-6 mb-8">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-[#0072CE]/10 border border-[#0072CE]/20 rounded text-[10px] uppercase tracking-[0.22em] font-bold text-[#0072CE]">
              <FileText size={11} /> Specification Sheet
            </div>
            <h1
              className={`mt-3 font-display ${
                compact ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"
              } font-semibold text-slate-900 tracking-tight leading-tight`}
              data-testid="spec-product-description"
            >
              {product.description}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
              <span className="font-mono font-semibold text-slate-900">{product.infoshop_pn}</span>
              <span className="text-slate-300">·</span>
              <span>{product.brand}</span>
              <span className="text-slate-300">·</span>
              <span>{product.category}</span>
            </div>
          </div>
          <div className="text-right shrink-0 hidden sm:block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Issued by</div>
            <div className="font-display font-semibold text-slate-900 text-sm mt-0.5">Infosys OMNISupply</div>
            <div className="text-[11px] text-slate-500">UniBuy™ · AI-Native Digital Procurement</div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Document type</div>
            <div className="font-mono text-xs text-slate-700 mt-0.5">technical-datasheet · v1</div>
          </div>
        </div>
      </header>

      {/* Image + identification */}
      <section className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-8">
        <div className="md:col-span-5">
          <div className="aspect-square border border-slate-200 rounded-xl overflow-hidden bg-white relative">
            {product.image_url ? (
              <ProductVisual product={product} className="absolute inset-0 w-full h-full" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-300">
                <ImageIcon size={48} strokeWidth={1.2} />
              </div>
            )}
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 inline-flex items-center gap-1.5">
            <Tag size={12} /> Identification
          </div>
          <Field label="UniBuy™ PN" value={product.infoshop_pn} mono testid="spec-infoshop-pn" />
          <Field label="Mfg Part Number" value={product.mfg_pn} mono testid="spec-mfg-pn" />
          <Field label="Brand / Manufacturer" value={product.brand} testid="spec-brand" />
          <Field label="Category" value={product.category} testid="spec-category" />
          <Field label="UNSPSC" value={product.unspsc} mono testid="spec-unspsc" />
        </div>
      </section>

      {/* Technical block */}
      <section className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 inline-flex items-center gap-1.5">
          <Box size={12} /> Technical
        </div>
        <Field label="Unit of Measure" value={product.uom} mono testid="spec-uom" />
        <Field label="Dimensions" value={product.dimensions} testid="spec-dimensions" />
        <Field
          label="Lead Time"
          value={`${product.lead_time_days} business days`}
          testid="spec-lead-time"
        />
      </section>

      {/* Commercial — OEM/internal split is NEVER disclosed; only customer-facing fields appear */}
      <section className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 inline-flex items-center gap-1.5">
          <Layers size={12} /> Commercial framework
        </div>
        <Field label="List Price (OEM)" value={money(product.list_price)} mono testid="spec-list-price" />
        <Field
          label="Customer Discount vs List"
          value={pct(product.effective_customer_discount_pct)}
          mono
          testid="spec-eff-discount"
        />
        <Field
          label="UniBuy™ Selling Price"
          value={money(product.selling_price)}
          mono
          testid="spec-selling-price"
        />
        <Field label="Currency" value={product.currency} mono testid="spec-currency" />
      </section>

      {/* Supply chain */}
      <section className="mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 inline-flex items-center gap-1.5">
          <Clock size={12} /> Supply chain commitments
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 leading-relaxed space-y-2">
          <div className="inline-flex items-start gap-2">
            <CircleCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>Sourced from the authorized OEM / tier-one distributor under the master Infosys OMNISupply commercial framework.</span>
          </div>
          <div className="inline-flex items-start gap-2">
            <CircleCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>
              Order-to-cash cycle: PunchOut/cXML in → PO acceptance → ASN → goods receipt → invoice match against the host ERP. Status transitions:
              <span className="ml-1 font-mono text-xs">Pending PO → Pending PO Acceptance → Pending Shipment → Shipped → Delivered</span>.
            </span>
          </div>
          <div className="inline-flex items-start gap-2">
            <CircleCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>
              Quality &amp; warranty are honored upstream by {product.brand}. RMA and warranty claims are raised through UniBuy™ Support and routed back to the OEM.
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-10 pt-6 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
        <div>Infosys OMNISupply / UniBuy™ · {product.infoshop_pn} · technical-datasheet · v1</div>
        <div className="font-mono">© Infosys Limited</div>
      </footer>
    </div>
  );
}
